import { Vec2 } from '../math/vec2.js';
import { circleCircle, wrapPosition } from '../math/collision.js';
import { createProjectile, createCratesFromAsteroid, createRandomAsteroid, getNextEntityId, getRandomStationWaypoint, } from './factories.js';
import { ARENA_WIDTH, ARENA_HEIGHT, SHIP_THRUST_FORCE, SHIP_MAX_SPEED, SHIP_ROTATION_SPEED, SHIP_DRAG, SHIP_TAIL_DRAG_PENALTY, SHIP_TAIL_ROTATION_PENALTY, SHIP_RADIUS, SHIP_MAX_HP, SHIP_INVULNERABLE_TIME, SHIP_RESPAWN_TIME, FIRE_COOLDOWN, PROJECTILE_RADIUS, PROJECTILE_DAMAGE, CRATE_SIZE, CRATE_DRAG, CRATE_PICKUP_COOLDOWN, STATION_MOVE_SPEED, STATION_TURN_IN_RADIUS, STATION_WAYPOINT_CHANGE_TIME, TAIL_SEGMENT_SPACING, TAIL_MAX_LENGTH, POINTS_ASTEROID_DESTROYED, POINTS_ORE_TURNED_IN, POINTS_PLAYER_DESTROYED, BONUS_TURN_IN_THRESHOLD, BONUS_TURN_IN_MULTIPLIER, } from '../constants/game.js';
/**
 * Normalize angle to [-PI, PI]
 */
function normalizeAngle(angle) {
    while (angle > Math.PI)
        angle -= 2 * Math.PI;
    while (angle < -Math.PI)
        angle += 2 * Math.PI;
    return angle;
}
/**
 * System: Apply player input to their ship
 */
export function systemShipMovement(world, inputs, dt) {
    for (const ship of world.ships) {
        if (!ship.alive)
            continue;
        const input = inputs.get(ship.playerId);
        if (!input)
            continue;
        // Calculate penalties based on tail length
        const tailLength = ship.tailSegments.length;
        const rotationMult = 1 / (1 + tailLength * SHIP_TAIL_ROTATION_PENALTY);
        const dragMult = 1 + tailLength * SHIP_TAIL_DRAG_PENALTY;
        // Handle rotation: either manual rotation or smooth turn toward moveAngle
        if (input.rotation !== 0) {
            // Manual rotation (keyboard/gamepad)
            ship.rot += input.rotation * SHIP_ROTATION_SPEED * rotationMult * dt;
        }
        else if (input.moveAngle !== undefined) {
            // Touch controls: smooth rotation toward moveAngle
            const angleDiff = normalizeAngle(input.moveAngle - ship.rot);
            const turnSpeed = SHIP_ROTATION_SPEED * 1.5 * rotationMult; // Slightly faster for touch
            if (Math.abs(angleDiff) > 0.05) {
                // Turn toward target angle
                const maxTurn = turnSpeed * dt;
                const actualTurn = Math.sign(angleDiff) * Math.min(maxTurn, Math.abs(angleDiff));
                ship.rot += actualTurn;
            }
        }
        // Apply thrust
        if (input.thrust !== 0) {
            const thrustDir = Vec2.fromAngle(ship.rot);
            const thrustForce = thrustDir.scale(SHIP_THRUST_FORCE * input.thrust);
            ship.vel = ship.vel.add(thrustForce.scale(dt));
        }
        // Apply drag (exponential decay)
        const dragFactor = Math.pow(SHIP_DRAG, dt * dragMult);
        ship.vel = ship.vel.scale(dragFactor);
        // Clamp speed
        const speed = ship.vel.length();
        if (speed > SHIP_MAX_SPEED) {
            ship.vel = ship.vel.normalize().scale(SHIP_MAX_SPEED);
        }
        // Update position
        ship.pos = ship.pos.add(ship.vel.scale(dt));
        // Wrap around arena
        ship.pos = wrapPosition(ship.pos, ARENA_WIDTH, ARENA_HEIGHT);
        // Update tail segments (follow the leader)
        updateShipTail(ship);
        // Update fire cooldown
        if (ship.fireCooldown > 0) {
            ship.fireCooldown -= dt;
        }
    }
}
/**
 * Update ship tail segments to follow the ship
 */
function updateShipTail(ship) {
    if (ship.tailSegments.length === 0)
        return;
    let leaderPos = ship.pos;
    let leaderRot = ship.rot;
    for (const segment of ship.tailSegments) {
        const toLeader = leaderPos.sub(segment.pos);
        const dist = toLeader.length();
        if (dist > TAIL_SEGMENT_SPACING) {
            const move = toLeader.normalize().scale(dist - TAIL_SEGMENT_SPACING);
            segment.pos = segment.pos.add(move);
        }
        // Face toward leader
        if (dist > 0.1) {
            segment.rot = toLeader.angle();
        }
        leaderPos = segment.pos;
        leaderRot = segment.rot;
    }
}
/**
 * System: Handle shooting
 */
export function systemShooting(world, inputs, dt) {
    for (const ship of world.ships) {
        if (!ship.alive)
            continue;
        const input = inputs.get(ship.playerId);
        if (!input || !input.shooting)
            continue;
        // Check cooldown
        if (ship.fireCooldown > 0)
            continue;
        // Use aimAngle if provided (non-zero), otherwise use ship rotation
        // This allows mouse aiming to shoot in the mouse direction
        const shootAngle = input.aimAngle !== 0 ? input.aimAngle : ship.rot;
        // Spawn projectile in the direction we're shooting
        const spawnOffset = Vec2.fromAngle(shootAngle).scale(SHIP_RADIUS + 5);
        const spawnPos = ship.pos.add(spawnOffset);
        const projectile = createProjectile(getNextEntityId(world), spawnPos, shootAngle, ship.playerId);
        world.projectiles.push(projectile);
        ship.fireCooldown = FIRE_COOLDOWN;
    }
}
/**
 * System: Move projectiles
 */
export function systemProjectilesMove(world, dt) {
    for (let i = world.projectiles.length - 1; i >= 0; i--) {
        const proj = world.projectiles[i];
        // Move
        proj.pos = proj.pos.add(proj.vel.scale(dt));
        // Update lifetime
        proj.lifetime -= dt;
        // Remove if expired or out of bounds
        if (proj.lifetime <= 0 ||
            proj.pos.x < -50 ||
            proj.pos.x > ARENA_WIDTH + 50 ||
            proj.pos.y < -50 ||
            proj.pos.y > ARENA_HEIGHT + 50) {
            world.projectiles.splice(i, 1);
        }
    }
}
/**
 * System: Handle collisions
 */
export function systemCollisions(world, dt) {
    // Projectile vs Asteroid
    for (let pi = world.projectiles.length - 1; pi >= 0; pi--) {
        const proj = world.projectiles[pi];
        for (let ai = world.asteroids.length - 1; ai >= 0; ai--) {
            const asteroid = world.asteroids[ai];
            if (circleCircle(proj.pos, PROJECTILE_RADIUS, asteroid.pos, asteroid.radius)) {
                // Damage asteroid
                asteroid.hp -= PROJECTILE_DAMAGE;
                // Remove projectile
                world.projectiles.splice(pi, 1);
                // Award points to shooter
                const shooter = world.ships.find((s) => s.playerId === proj.ownerId);
                if (shooter) {
                    shooter.score += POINTS_ASTEROID_DESTROYED;
                }
                // Check if asteroid destroyed
                if (asteroid.hp <= 0) {
                    // Spawn crates
                    const crates = createCratesFromAsteroid(getNextEntityId(world), asteroid.pos, asteroid.size);
                    // Update nextEntityId for all spawned crates
                    world.nextEntityId += crates.length;
                    world.crates.push(...crates);
                    // Remove asteroid
                    world.asteroids.splice(ai, 1);
                }
                break; // Projectile is gone, stop checking
            }
        }
    }
    // Projectile vs Ship (other players)
    for (let pi = world.projectiles.length - 1; pi >= 0; pi--) {
        const proj = world.projectiles[pi];
        for (const ship of world.ships) {
            if (!ship.alive)
                continue;
            if (ship.playerId === proj.ownerId)
                continue; // Can't shoot yourself
            if (ship.invulnerableTimer > 0)
                continue; // Skip if invulnerable
            // Check collision with ship body
            if (circleCircle(proj.pos, PROJECTILE_RADIUS, ship.pos, SHIP_RADIUS)) {
                // Apply damage to ship
                ship.hp--;
                ship.damageLevel++;
                ship.invulnerableTimer = SHIP_INVULNERABLE_TIME;
                // Remove projectile
                world.projectiles.splice(pi, 1);
                // Check if ship destroyed (4th hit)
                if (ship.hp <= 0) {
                    // Drop all crates from tail
                    dropCratesFromShip(world, ship, ship.tailSegments.length);
                    // Award kill points only on destruction
                    const shooter = world.ships.find((s) => s.playerId === proj.ownerId);
                    if (shooter) {
                        shooter.score += POINTS_PLAYER_DESTROYED;
                        shooter.kills++;
                    }
                    // Kill ship and start respawn timer
                    ship.alive = false;
                    ship.respawnTimer = SHIP_RESPAWN_TIME;
                }
                break;
            }
            // Check collision with tail segments
            for (let ti = ship.tailSegments.length - 1; ti >= 0; ti--) {
                const segment = ship.tailSegments[ti];
                if (circleCircle(proj.pos, PROJECTILE_RADIUS, segment.pos, CRATE_SIZE)) {
                    // Drop this segment and all after it
                    dropCratesFromShip(world, ship, ship.tailSegments.length - ti);
                    world.projectiles.splice(pi, 1);
                    break;
                }
            }
        }
    }
    // Ship vs Crate (pickup)
    for (const ship of world.ships) {
        if (!ship.alive)
            continue;
        if (ship.tailSegments.length >= TAIL_MAX_LENGTH)
            continue;
        for (let ci = world.crates.length - 1; ci >= 0; ci--) {
            const crate = world.crates[ci];
            // Skip if on cooldown from this player
            if (crate.pickupCooldown > 0 && crate.lastOwnerId === ship.playerId)
                continue;
            if (circleCircle(ship.pos, SHIP_RADIUS + 5, crate.pos, CRATE_SIZE)) {
                // Add to tail
                const lastPos = ship.tailSegments.length > 0
                    ? ship.tailSegments[ship.tailSegments.length - 1].pos
                    : ship.pos;
                ship.tailSegments.push({
                    pos: lastPos.clone(),
                    rot: ship.rot,
                    value: crate.value,
                });
                // Remove crate
                world.crates.splice(ci, 1);
            }
        }
    }
}
/**
 * Drop crates from a ship's tail
 */
function dropCratesFromShip(world, ship, count) {
    const toDrop = Math.min(count, ship.tailSegments.length);
    for (let i = 0; i < toDrop; i++) {
        const segment = ship.tailSegments.pop();
        // Create crate with scatter velocity
        const scatterAngle = Math.random() * Math.PI * 2;
        const scatterSpeed = 50 + Math.random() * 100;
        const vel = Vec2.fromAngle(scatterAngle).scale(scatterSpeed);
        const crate = {
            id: getNextEntityId(world),
            pos: segment.pos.clone(),
            vel,
            value: segment.value,
            pickupCooldown: CRATE_PICKUP_COOLDOWN,
            lastOwnerId: ship.playerId,
        };
        world.crates.push(crate);
    }
}
/**
 * System: Update crates
 */
export function systemCrates(world, dt) {
    for (const crate of world.crates) {
        // Move
        crate.pos = crate.pos.add(crate.vel.scale(dt));
        // Apply drag
        crate.vel = crate.vel.scale(Math.pow(CRATE_DRAG, dt));
        // Wrap position
        crate.pos = wrapPosition(crate.pos, ARENA_WIDTH, ARENA_HEIGHT);
        // Update cooldown
        if (crate.pickupCooldown > 0) {
            crate.pickupCooldown -= dt;
        }
    }
}
/**
 * System: Update asteroids
 */
export function systemAsteroids(world, dt) {
    for (const asteroid of world.asteroids) {
        // Move
        asteroid.pos = asteroid.pos.add(asteroid.vel.scale(dt));
        // Rotate
        asteroid.rot += asteroid.angularVel * dt;
        // Wrap position
        asteroid.pos = wrapPosition(asteroid.pos, ARENA_WIDTH, ARENA_HEIGHT);
    }
}
/**
 * System: Update station
 */
export function systemStation(world, dt) {
    const station = world.station;
    // Move toward waypoint
    const toTarget = station.targetPos.sub(station.pos);
    const dist = toTarget.length();
    if (dist > 5) {
        const moveDir = toTarget.normalize();
        station.vel = moveDir.scale(STATION_MOVE_SPEED);
        station.pos = station.pos.add(station.vel.scale(dt));
    }
    else {
        station.vel = Vec2.zero();
    }
    // Update waypoint timer
    station.waypointTimer += dt;
    if (station.waypointTimer >= STATION_WAYPOINT_CHANGE_TIME) {
        station.targetPos = getRandomStationWaypoint();
        station.waypointTimer = 0;
    }
}
/**
 * System: Handle station turn-in
 * Scoring: 1 cent per crate, bonus 10 cents per crate if turning in 5+
 */
export function systemStationTurnIn(world) {
    const station = world.station;
    for (const ship of world.ships) {
        if (!ship.alive)
            continue;
        if (ship.tailSegments.length === 0)
            continue;
        // Check if in turn-in range
        const dist = ship.pos.distanceTo(station.pos);
        if (dist <= STATION_TURN_IN_RADIUS) {
            const crateCount = ship.tailSegments.length;
            // Base points: 1 cent per crate
            let points = crateCount * POINTS_ORE_TURNED_IN;
            // Bonus: If turning in 5+ crates, get 10 cents extra per crate
            if (crateCount >= BONUS_TURN_IN_THRESHOLD) {
                points += crateCount * BONUS_TURN_IN_MULTIPLIER;
            }
            ship.score += points;
            ship.totalOreTurnedIn += crateCount;
            // Clear tail
            ship.tailSegments = [];
        }
    }
}
/**
 * System: Update ship timers (invulnerability, respawn)
 */
export function systemShipTimers(world, dt) {
    for (const ship of world.ships) {
        // Update invulnerability timer
        if (ship.invulnerableTimer > 0) {
            ship.invulnerableTimer -= dt;
        }
        // Update respawn timer for dead ships
        if (!ship.alive && ship.respawnTimer > 0) {
            ship.respawnTimer -= dt;
            // Respawn when timer expires
            if (ship.respawnTimer <= 0) {
                ship.alive = true;
                ship.hp = SHIP_MAX_HP;
                ship.damageLevel = 0;
                ship.invulnerableTimer = SHIP_INVULNERABLE_TIME; // Brief spawn protection
                // Respawn at random position
                ship.pos = new Vec2(ARENA_WIDTH * 0.1 + Math.random() * ARENA_WIDTH * 0.8, ARENA_HEIGHT * 0.1 + Math.random() * ARENA_HEIGHT * 0.8);
                ship.vel = Vec2.zero();
                ship.rot = Math.random() * Math.PI * 2;
            }
        }
    }
}
/**
 * System: Respawn asteroids if too few
 */
export function systemSpawning(world, targetAsteroidCount = 15) {
    while (world.asteroids.length < targetAsteroidCount) {
        const asteroid = createRandomAsteroid(getNextEntityId(world));
        world.asteroids.push(asteroid);
    }
}
/**
 * System: Update match timer
 */
export function systemMatchTimer(world, dt) {
    world.timeRemaining -= dt;
    world.tick++;
    return world.timeRemaining <= 0;
}
/**
 * Run all systems in order for a single tick
 */
export function runSystems(world, inputs, dt) {
    systemShipTimers(world, dt);
    systemShipMovement(world, inputs, dt);
    systemShooting(world, inputs, dt);
    systemProjectilesMove(world, dt);
    systemAsteroids(world, dt);
    systemCrates(world, dt);
    systemStation(world, dt);
    systemCollisions(world, dt);
    systemStationTurnIn(world);
    systemSpawning(world);
    return systemMatchTimer(world, dt);
}
//# sourceMappingURL=systems.js.map